import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { getFeedUrlIssues, resolveFeedUrlIssue } from '../../api/contentCompletion';
import { CompanyContentHealth, FeedUrlIssue } from '../../types/contentCompletion';
import { useNotification } from '../../context/NotificationContext';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';
import { NEEDS_REVIEW_ISSUE_TYPE } from './urlDiscoveryUtils';

const ISSUE_TYPE_LABELS: Record<string, string> = {
  zero_results: 'No results',
  fetch_failed: 'Fetch failed',
  unknown: 'Unknown',
  website_not_found: 'Website not found',
  broken_no_replacement: 'No replacement found',
  discovery_error: 'Discovery error',
  rejected_match: 'Rejected match',
};

const formatIssueType = (value: string) => ISSUE_TYPE_LABELS[value] ?? value.replace(/_/g, ' ');

const formatDate = (value?: string) => {
  if (!value) return '—';
  return new Date(value).toLocaleString();
};

interface FeedIssuesPanelProps {
  canEdit: boolean;
  refreshToken?: number;
  companies: CompanyContentHealth[];
  onSelectCompany: (company: CompanyContentHealth) => void;
}

const FeedIssuesPanel: React.FC<FeedIssuesPanelProps> = ({
  canEdit,
  refreshToken = 0,
  companies,
  onSelectCompany,
}) => {
  const { showNotification } = useNotification();
  const [expanded, setExpanded] = useState(true);
  const [issues, setIssues] = useState<FeedUrlIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const loadIssues = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getFeedUrlIssues('open');
      setIssues(data.issues.filter((issue) => issue.issue_type !== NEEDS_REVIEW_ISSUE_TYPE));
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        'Failed to load feed URL issues';
      setError(msg);
      setIssues([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadIssues();
  }, [loadIssues, refreshToken]);

  const handleResolve = async (issueId: string) => {
    setResolvingId(issueId);
    try {
      await resolveFeedUrlIssue(issueId);
      setIssues((prev) => prev.filter((issue) => issue.id !== issueId));
      showNotification('Issue marked as resolved', 'success');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        'Failed to resolve issue';
      showNotification(msg, 'error');
    } finally {
      setResolvingId(null);
    }
  };

  const handleOpenCompany = (issue: FeedUrlIssue) => {
    const existing = companies.find((company) => company.company_id === issue.company_id);
    if (existing) {
      onSelectCompany(existing);
      return;
    }
    onSelectCompany({
      company_id: issue.company_id,
      company_name: issue.company_name,
      press_release_url: issue.source_url,
      has_feed_configured: true,
      pr_total_count: 0,
      pr_recent_count: 0,
      quartr_total_count: 0,
      quartr_recent_count: 0,
      has_quartr_feed: false,
      status: 'no_feed',
      quartr_status: 'no_feed',
    });
  };

  if (!canEdit && !loading && !error && issues.length === 0) {
    return null;
  }

  const showEmptyState = !loading && !error && issues.length === 0;

  return (
    <Paper sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: expanded ? 1 : 0,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            URL / feed issues
          </Typography>
          {!loading && (
            <Chip label={issues.length} size="small" color={issues.length > 0 ? 'warning' : 'default'} />
          )}
        </Box>
        <IconButton
          size="small"
          onClick={() => setExpanded((prev) => !prev)}
          aria-label={expanded ? 'Collapse feed issues' : 'Expand feed issues'}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        {loading ? (
          <Box sx={{ py: 3 }}>
            <LoadingSpinner message="Loading reported issues..." />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2 }}>
            <ErrorMessage message={error} onDismiss={() => setError(null)} />
          </Box>
        ) : showEmptyState ? (
          <Box sx={{ px: 2, py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No URL or feed issues reported yet. Failed backfills and URL discovery checks are
              added automatically; you can also report rejected matches from the discovery review
              dialog.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Company</TableCell>
                  <TableCell>Source URL</TableCell>
                  <TableCell>Issue</TableCell>
                  <TableCell>Reported</TableCell>
                  <TableCell>Note</TableCell>
                  {canEdit && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {issues.map((issue) => (
                  <TableRow key={issue.id} hover>
                    <TableCell>
                      <Button
                        variant="text"
                        size="small"
                        sx={{ textTransform: 'none', fontWeight: 600, p: 0, minWidth: 0 }}
                        onClick={() => handleOpenCompany(issue)}
                      >
                        {issue.company_name}
                      </Button>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {issue.company_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                          variant="body2"
                          component="a"
                          href={issue.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ wordBreak: 'break-all' }}
                        >
                          {issue.source_url}
                        </Typography>
                        <OpenInNewIcon fontSize="inherit" color="action" />
                      </Box>
                    </TableCell>
                    <TableCell>{formatIssueType(issue.issue_type)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(issue.reported_at)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        by {issue.reported_by}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Typography variant="body2" color="text.secondary" noWrap title={issue.note}>
                        {issue.note || '—'}
                      </Typography>
                    </TableCell>
                    {canEdit && (
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={resolvingId === issue.id}
                          onClick={() => handleResolve(issue.id)}
                        >
                          {resolvingId === issue.id ? 'Resolving…' : 'Resolve'}
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Collapse>
    </Paper>
  );
};

export default FeedIssuesPanel;
